import { Select as SelectPrimitive } from '@base-ui/react'
import { Check, ChevronDown, Search, SearchX, X } from 'lucide-react'
import { useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CONTROL_ICON_SIZE,
  POPOVER_OPTION,
  controlShellClass,
  type ChoiceOption,
  type ControlSize,
} from '@/components/ui/control'
import { cn } from '@/lib/cn'

/* The select.
 *
 * A native `<select>` cannot be styled below the box — the dropdown is drawn by
 * the OS, in the OS's font, at the OS's row height — so in a product this
 * deliberate about type and density it was the one control that always looked
 * borrowed. Base UI's select primitive replaces the popup while keeping
 * everything the native element was actually good at, and those parts are now
 * ITS responsibility rather than this file's:
 *
 *   · `role="combobox"` + `role="listbox"`, so it is announced as a select.
 *   · ↑/↓ move, Home/End jump, Enter/Space commit, Escape reverts and closes.
 *   · Typeahead, highlight tracking, scroll-into-view.
 *   · Opening focuses the CURRENT value, so arrows move from where the eye is.
 *   · The hidden input that makes the control submit inside a plain `<form>`.
 *
 * What stays hand-written is the part no primitive ships: the `searchable`
 * filter box, its clear button, its screen-reader match count and the empty
 * state. Those keep the exact markup and translation keys they had.
 *
 * `searchable` is opt-in and off by default. A five-item mode list does not
 * need a filter, and adding one to every select would put a keystroke between
 * the user and a list they can already see in full.
 */

/** Re-exported under the name call sites expect; defined in control.ts so the
 *  helper that builds these can live outside a component file. */
export type SelectOption<T extends string = string> = ChoiceOption<T>

export interface SelectProps<T extends string = string> {
  value: T
  onChange: (value: T) => void
  options: ReadonlyArray<SelectOption<T>>
  id?: string
  name?: string
  disabled?: boolean
  placeholder?: string
  /** Adds a filter box inside the popover. Worth it past ~10 options. */
  searchable?: boolean
  selectSize?: ControlSize
  className?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-label'?: string
}

export function Select<T extends string = string>({
  value,
  onChange,
  options,
  id,
  name,
  disabled,
  placeholder,
  searchable = false,
  selectSize = 'md',
  className,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'aria-label': ariaLabel,
}: SelectProps<T>) {
  const { t } = useTranslation()

  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const selected = options.find((option) => option.value === value)

  const visible = useMemo(() => {
    if (!searchable || !query.trim()) return options
    const needle = query.trim().toLowerCase()
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.hint?.toLowerCase().includes(needle),
    )
  }, [options, query, searchable])

  return (
    <SelectPrimitive.Root<T>
      id={id}
      name={name}
      value={value}
      onValueChange={(next) => {
        // `null` is the primitive's "cleared"; this control has no clear
        // affordance, so it can only mean a no-op and is dropped.
        if (next !== null) onChange(next)
      }}
      disabled={disabled}
      /* Not modal. A modal select locks page scroll and blocks outside pointer
         events, and this control is routinely opened inside a Sheet that has
         already taken both of those decisions for the whole overlay. */
      modal={false}
      onOpenChange={(open) => {
        // The filter is per-opening. Leaving the previous query in place would
        // reopen the select onto a list the user did not filter this time.
        if (!open) setQuery('')
      }}
      onOpenChangeComplete={(open) => {
        /* `Select.Popup` exposes `finalFocus` but no `initialFocus`, so the
           search field is focused once the open transition has finished —
           earlier than that and the primitive's own focus move to the selected
           item lands after it and takes the caret back. */
        if (open && searchable) searchRef.current?.focus()
      }}
    >
      <SelectPrimitive.Trigger
        aria-describedby={describedBy}
        aria-invalid={invalid}
        aria-label={ariaLabel}
        className={cn(
          controlShellClass(selectSize),
          'flex cursor-pointer items-center justify-between gap-2 px-3 text-left',
          disabled && 'cursor-not-allowed',
          className,
        )}
      >
        <SelectPrimitive.Value
          className={cn(
            'min-w-0 flex-1 truncate',
            selected ? 'text-text-body' : 'text-text-faint',
          )}
        >
          {() => selected?.label ?? placeholder ?? t('common.form.selectPlaceholder')}
        </SelectPrimitive.Value>
        <ChevronDown
          size={14}
          aria-hidden
          className={cn(
            'text-text-muted shrink-0 transition-transform',
            'duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
            'data-popup-open:rotate-180',
          )}
        />
      </SelectPrimitive.Trigger>

      {/* Portaled for the same reason as the date picker's calendar: inside a
          sheet this listbox was clipped by the scrollable body. Width comes
          from `--anchor-width`, the primitive's own measurement of the trigger,
          which replaces the manual anchoring hook. */}
      <SelectPrimitive.Portal>
        <SelectPrimitive.Positioner
          sideOffset={4}
          align="start"
          /* Off: the default aligns the SELECTED ITEM with the trigger, which
             slides the whole popup up the screen as the value changes. This
             control has always opened below its box. */
          alignItemWithTrigger={false}
          className="z-[60]"
        >
          <SelectPrimitive.Popup
            aria-label={ariaLabel}
            className={cn(
              'bg-surface border-border-subtle rounded-md border p-1',
              'shadow-[var(--shadow-overlay)]',
              'w-(--anchor-width) max-w-(--available-width)',
              'origin-(--transform-origin)',
              'transition-[opacity,transform] duration-[var(--dur-fast)]',
              'ease-[var(--ease-standard)]',
              'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
              'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
            )}
          >
            {/* The filter is a CONTROL, not a strip of text above the list: a
                sunken field with its own border, the same fill/lift language
                as every other box in the system. The underlined version read
                as a heading, so the first click of a searchable select was
                spent discovering it could be typed into. The hairline below
                the field still separates the filter from the results — the
                field is what you type in, the list is what it produced. */}
            {searchable ? (
              <div className="border-line-hair mb-1 border-b px-1 pt-1 pb-2">
                <div
                  className={cn(
                    controlShellClass('sm'),
                    'group/search flex items-center gap-2 pr-1 pl-2.5',
                  )}
                >
                  <Search
                    size={CONTROL_ICON_SIZE}
                    aria-hidden
                    className={cn(
                      'text-text-faint pointer-events-none shrink-0 transition-colors',
                      'duration-[var(--dur-fast)]',
                      'group-focus-within/search:text-brand',
                    )}
                  />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    /* The popup is a composite widget and swallows printable
                       keys to drive its own typeahead, so every key that is
                       NOT navigation has to be stopped here or the field
                       receives nothing. The listed keys are let through
                       precisely so the list stays drivable from the field. */
                    onKeyDown={(event) => {
                      if (
                        event.key === 'Escape' ||
                        event.key === 'Enter' ||
                        event.key === 'Tab' ||
                        event.key === 'Home' ||
                        event.key === 'End' ||
                        event.key.startsWith('Arrow')
                      ) {
                        return
                      }
                      event.stopPropagation()
                    }}
                    placeholder={t('common.form.searchPlaceholder')}
                    aria-label={t('common.form.searchPlaceholder')}
                    // The count is live, so a screen reader hears "3 of 40"
                    // change as it types instead of having to leave the field
                    // and walk the list to find out what the filter did.
                    aria-describedby={`${listId}-count`}
                    className={cn(
                      'text-text-body min-w-0 flex-1 border-0 bg-transparent p-0',
                      'font-sans text-[12.5px] font-normal outline-none',
                      'focus-visible:shadow-none',
                    )}
                  />
                  {/* Clearing restores the full list in one press. Out of the
                      tab order for the same reason as Input's: Tab here means
                      "leave the select", and it must keep meaning that. */}
                  {query ? (
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={t('common.form.clear')}
                      onPointerDown={(event) => {
                        // Before the outside-press handler sees it, and before
                        // the field loses focus to the button.
                        event.preventDefault()
                        setQuery('')
                        searchRef.current?.focus()
                      }}
                      className="text-text-faint hover:text-text-body flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-[4px] transition-colors duration-[var(--dur-fast)]"
                    >
                      <X size={12} aria-hidden />
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* What the filter did, for the screen reader only. `aria-live` is
                absent on purpose — aria-describedby on a live region is read
                on change by the field itself, and adding a second announcer
                would say the count twice per keystroke. */}
            {searchable ? (
              <span id={`${listId}-count`} className="sr-only">
                {t('common.form.matchCount', {
                  count: visible.length,
                  total: options.length,
                })}
              </span>
            ) : null}

            <SelectPrimitive.List className="asa-scroll max-h-[260px] overflow-y-auto">
              {visible.length === 0 ? (
                /* Centred and given air, so an empty result reads as an
                   ANSWER rather than as one greyed-out row the eye tries to
                   click. The two lines say what happened and what to do; the
                   second is dropped when the list was empty to begin with,
                   where clearing a filter would change nothing. */
                <div className="flex flex-col items-center gap-0.5 px-3 py-6 text-center">
                  <SearchX size={18} aria-hidden className="text-text-faint mb-0.5" />
                  <span className="text-text-body text-[12.5px] font-normal">
                    {t('common.form.noMatches')}
                  </span>
                  {searchable && query ? (
                    <span className="text-text-muted text-[11.5px] font-light">
                      {t('common.form.noMatchesHint')}
                    </span>
                  ) : null}
                </div>
              ) : (
                visible.map((option) => (
                  <SelectPrimitive.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={cn(
                      POPOVER_OPTION,
                      'data-highlighted:bg-surface-hover',
                      'data-disabled:cursor-not-allowed data-disabled:opacity-50',
                      'data-selected:text-text-heading data-selected:font-medium',
                      'text-text-body font-normal',
                    )}
                  >
                    <SelectPrimitive.ItemText
                      render={<span className="flex min-w-0 flex-1 flex-col" />}
                    >
                      <span className="truncate">{option.label}</span>
                      {option.hint ? (
                        <span className="text-text-muted truncate text-[11.5px] font-light">
                          {option.hint}
                        </span>
                      ) : null}
                    </SelectPrimitive.ItemText>
                    {/* Redundant with aria-selected, hence aria-hidden:
                        this tick is for the eye. */}
                    <SelectPrimitive.ItemIndicator
                      render={<span className="shrink-0" />}
                    >
                      <Check size={14} aria-hidden className="text-brand" />
                    </SelectPrimitive.ItemIndicator>
                  </SelectPrimitive.Item>
                ))
              )}
            </SelectPrimitive.List>
          </SelectPrimitive.Popup>
        </SelectPrimitive.Positioner>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

/* `toOptions` lives in control.ts, not here: this file may only export
   components, or Vite's fast refresh silently stops working for every control
   in it (react-refresh/only-export-components). */
